/**
 * Field validation rules for specific input types
 */

import { ValidationRule } from '@utils/validation'

// Field type constants
export const FieldTypes = {
  TEXT: 'text',
  EMAIL: 'email',
  PHONE: 'phone',
  TEL: 'tel',
  PASSWORD: 'password',
  NUMBER: 'number',
  DATE: 'date',
  SELECT: 'select',
  TEXTAREA: 'textarea',
  CHECKBOX: 'checkbox',
  RADIO: 'radio',
} as const

// Common validation rules for different field types
export const getFieldValidationRules = (fieldType: string, fieldName?: string): ValidationRule => {
  switch (fieldType) {
    case FieldTypes.EMAIL:
      return {
        required: true,
        pattern: /^[a-zA-Z0-9]([a-zA-Z0-9._+-]*[a-zA-Z0-9])?@[a-zA-Z0-9]([a-zA-Z0-9.-]*[a-zA-Z0-9])?\.[a-zA-Z]{2,}$/,
        custom: (value: string) => {
          if (value && value.includes('..')) {
            return 'Please enter a valid email address'
          }
          return true
        },
      }

    case FieldTypes.PHONE:
    case FieldTypes.TEL:
      return {
        required: true,
        minLength: 10,
        pattern: /^[\d\s\-()+]+$/,
        custom: (value: string) => {
          const cleaned = value.replace(/\D/g, '')
          if (cleaned.length < 10) {
            return 'Phone number must be at least 10 digits'
          }
          return true
        },
      }

    case FieldTypes.PASSWORD:
      return {
        required: true,
        minLength: 8,
        custom: (value: string) => {
          // At least one number, one letter
          if (!/(?=.*[A-Za-z])(?=.*\d)/.test(value)) {
            return 'Password must contain at least one letter and one number'
          }
          return true
        },
      }

    case FieldTypes.TEXT:
      // Special handling for specific field names
      if (fieldName) {
        switch (fieldName) {
          case 'first_name':
          case 'last_name':
            return {
              required: true,
              minLength: 2,
              maxLength: 50,
              pattern: /^[a-zA-Z\s'-]+$/,
            }

          case 'address':
          case 'address1':
            return {
              required: true,
              minLength: 5,
              maxLength: 100,
            }

          case 'city':
            return {
              required: true,
              minLength: 2,
              maxLength: 50,
              pattern: /^[a-zA-Z\s'-]+$/,
            }

          case 'zip':
          case 'postal_code':
            return {
              required: true,
              minLength: 3,
              maxLength: 10,
              pattern: /^[A-Za-z0-9\s-]+$/,
            }

          case 'state':
            return {
              required: true,
              minLength: 2,
              maxLength: 50,
            }
        }
      }

      return {
        required: false,
        maxLength: 255,
      }

    case FieldTypes.NUMBER:
      return {
        required: false,
        custom: (value: string) => {
          if (value && isNaN(Number(value))) {
            return 'Please enter a valid number'
          }
          return true
        },
      }

    default:
      return {
        required: false,
      }
  }
}

// Country-specific field validation
export const getCountryFieldRules = (country: string, fieldName: string): Partial<ValidationRule> => {
  switch (country) {
    case 'US':
      if (fieldName === 'zip') {
        return {
          pattern: /^\d{5}(-\d{4})?$/,
          custom: (value: string) => {
            if (value && !/^\d{5}(-\d{4})?$/.test(value)) {
              return 'Please enter a valid US ZIP code (12345 or 12345-6789)'
            }
            return true
          },
        }
      }
      break

    case 'CA':
      if (fieldName === 'zip' || fieldName === 'postal_code') {
        return {
          pattern: /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/,
          custom: (value: string) => {
            if (value && !/^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(value)) {
              return 'Please enter a valid Canadian postal code (A1A 1A1)'
            }
            return true
          },
        }
      }
      break

    case 'MX':
      if (fieldName === 'company') {
        return {
          required: true,
          minLength: 2,
          maxLength: 100,
          custom: (value: string) => {
            if (!value || value.trim().length < 2) {
              return 'RFC company name is required for Mexico'
            }
            return true
          },
        }
      }
      break
  }

  return {}
}

// Field formatting helpers
export const formatFieldValue = (value: string, fieldType: string, country?: string): string => {
  switch (fieldType) {
    case FieldTypes.PHONE:
    case FieldTypes.TEL:
      return formatPhoneNumber(value)

    case FieldTypes.TEXT:
      if (country && country === 'CA' && value) {
        // Canadian postal code formatting
        const cleaned = value.replace(/\s+/g, '').toUpperCase()
        if (cleaned.length === 6) {
          return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`
        }
      }
      return value

    default:
      return value
  }
}

// Phone number formatting
export const formatPhoneNumber = (value: string): string => {
  const cleaned = value.replace(/\D/g, '')

  if (cleaned.length >= 10) {
    if (cleaned.length === 10) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
    } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return cleaned.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '+$1 ($2) $3-$4')
    }
  }

  return value
}

// Get field placeholder based on type and context
export const getFieldPlaceholder = (fieldType: string, fieldName: string, country?: string): string => {
  switch (fieldType) {
    case FieldTypes.EMAIL:
      return 'example@domain.com'

    case FieldTypes.PHONE:
    case FieldTypes.TEL:
      switch (country) {
        case 'CA':
          return '(555) 123-4567'
        case 'MX':
          return '+52 55 1234 5678'
        default:
          return '(555) 555-5555'
      }

    case FieldTypes.TEXT:
      if (fieldName === 'zip' || fieldName === 'postal_code') {
        switch (country) {
          case 'CA':
            return 'A1A 1A1'
          case 'US':
            return '12345'
          default:
            return 'ZIP/Postal Code'
        }
      }

      switch (fieldName) {
        case 'first_name':
          return 'First Name'
        case 'last_name':
          return 'Last Name'
        case 'address':
        case 'address1':
          return '123 Main Street'
        case 'city':
          return 'City'
        case 'state':
          return country === 'CA' ? 'Province' : 'State'
        case 'company':
          return country === 'MX' ? 'RFC Company Name' : 'Company'
        default:
          return fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      }

    default:
      return ''
  }
}

export default {
  FieldTypes,
  getFieldValidationRules,
  getCountryFieldRules,
  formatFieldValue,
  formatPhoneNumber,
  getFieldPlaceholder,
}
